import React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import {
  useInternalCaseNotes,
  useCreateCaseNote,
  useUpdateCaseNote,
  useDeleteCaseNote,
  useUploadNoteAttachment,
} from '../../../data/support/useSupport';
import { SupportApi } from '../../../data/support/supportApi';
import { CaseNoteAttachment } from '../../../types/support.types';
import CaseNote from './CaseNote';
import CaseMessageComposer from './CaseMessageComposer';

interface SupportCaseNotesProps {
  caseId: string;
}

const SupportCaseNotes: React.FC<SupportCaseNotesProps> = ({ caseId }) => {
  const notesQuery = useInternalCaseNotes(caseId);
  const notes = notesQuery.data || [];
  const createNote = useCreateCaseNote(caseId);
  const updateNote = useUpdateCaseNote(caseId);
  const deleteNote = useDeleteCaseNote(caseId);
  const uploadNoteAttachment = useUploadNoteAttachment(caseId);

  const handleCreate = async (body: Record<string, any>, files?: File[]) => {
    const result = await createNote.mutateAsync({ body });
    if (files?.length && result?.id) {
      await Promise.all(
        files.map((file) =>
          uploadNoteAttachment.mutateAsync({ noteId: result.id, file }),
        ),
      );
    }
  };

  const handleUpdate = (noteId: string, body: Record<string, any>) => {
    updateNote.mutate({ noteId, data: { body } });
  };

  const handleDelete = (noteId: string) => {
    deleteNote.mutate(noteId);
  };

  const handleDownloadAttachment = async (
    noteId: string,
    attachment: CaseNoteAttachment,
  ) => {
    const result = await SupportApi.internalGetNoteAttachmentUrl(
      caseId,
      noteId,
      attachment.id,
    );
    const url = result.data.attributes.download_url;
    window.open(url, '_blank');
  };

  const getAttachmentUrl = async (
    noteId: string,
    attachment: CaseNoteAttachment,
  ): Promise<string> => {
    const result = await SupportApi.internalGetNoteAttachmentUrl(
      caseId,
      noteId,
      attachment.id,
    );
    return result.data.attributes.download_url;
  };

  if (notesQuery.isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 4,
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: 'rgba(255, 193, 7, 0.04)', borderRadius: 1, p: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 2 }}>
        Internal Notes
      </Typography>

      {notes.length === 0 && (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          No notes yet
        </Typography>
      )}

      {notes.map((note) => (
        <CaseNote
          key={note.id}
          note={note}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
          isUpdating={updateNote.isPending}
          onDownloadAttachment={(att) => handleDownloadAttachment(note.id, att)}
          getAttachmentUrl={(att) => getAttachmentUrl(note.id, att)}
        />
      ))}

      <Box sx={{ mt: 2 }}>
        <CaseMessageComposer
          onSend={handleCreate}
          isPending={createNote.isPending || uploadNoteAttachment.isPending}
          placeholder="Add a note..."
        />
      </Box>
    </Box>
  );
};

export default SupportCaseNotes;
